import React from 'react';
import useIsMounted from '../../hooks/useIsMounted';
import {
  calcHitachiFinanceProperties,
  calcMaxDeposit,
  filterByDepositValue,
  findDeferredTerm,
  mapByAddingMinAndMaxDepositAmount
} from './hitachiFinanceCalculations';
import ajaxRequest from '../../helpers/ajaxRequest';
import { FinanceTermTypes, HitachiFinanceData, HitachiFinanceTerm, PaymentType } from '../../types';

interface UseHitachiFinanceDataProps {
  totalPrice?: number | null;
  financeDetailsUrl: string;
  initialFinanceData?: HitachiFinanceData | null;
}

function useHitachiFinanceData({
  financeDetailsUrl,
  totalPrice = null,
  initialFinanceData = null
}: UseHitachiFinanceDataProps): HitachiFinanceData {
  const { hitachiDepositEnabled: depositEnabled = false } = window.bbGlobal || {};
  const isMountedRef = useIsMounted();
  const prevFinanceDetailsApiRef = React.useRef<string>('');
  const prevTotalPriceRef = React.useRef<number | null>(null);
  const [terms, setTerms] = React.useState<HitachiFinanceTerm[]>([]);
  const [priceValue, setPriceValue] = React.useState<number | null>(null);
  const maxDeposit = React.useMemo(() => calcMaxDeposit(terms), [terms]);
  const [depositValue, setDepositValue] = React.useState<number | null>(
    initialFinanceData ? initialFinanceData.depositValue : null
  );
  const depositValueInRange = depositValue && maxDeposit && depositValue > maxDeposit ? maxDeposit : depositValue;
  const termsInRage = React.useMemo(
    () => filterByDepositValue(terms, depositValueInRange || 0),
    [terms, depositValueInRange]
  );
  const deferredTerm = React.useMemo(
    () => findDeferredTerm(terms, depositValueInRange || 0),
    [terms, depositValueInRange]
  );
  const [isDeferred, setIsPaymentDeferred] = React.useState(
    initialFinanceData ? initialFinanceData.isPaymentDeferred : false
  );
  const isPaymentDeferred = isDeferred || (termsInRage.length === 0 && !!deferredTerm);
  const [selectedTerm, setSelectedTerm] = React.useState(initialFinanceData ? initialFinanceData.selectedTerm : null);
  const mappedSelectedTerm = React.useMemo(
    () =>
      selectedTerm && termsInRage.indexOf(selectedTerm) !== -1
        ? selectedTerm
        : termsInRage[termsInRage.length - 1] || null,
    [selectedTerm, termsInRage]
  );
  const activeTerm = isPaymentDeferred && deferredTerm ? deferredTerm : mappedSelectedTerm;
  const mappedDepositValue: number | null = React.useMemo(() => {
    if (activeTerm) {
      return depositValueInRange !== null && depositValueInRange >= activeTerm.minDepositAmount
        ? depositValueInRange
        : activeTerm.defaultDepositAmount;
    }
    return null;
  }, [depositValueInRange, activeTerm]);
  const [isFixedDepositSelected, setIsFixedDepositSelected] = React.useState(
    initialFinanceData ? initialFinanceData.isFixedDepositSelected : false
  );
  const fixedDepositAmount = activeTerm ? activeTerm.fixedDepositAmount : null;
  const financeProperties = React.useMemo(() => {
    const deposit = isFixedDepositSelected && fixedDepositAmount !== null ? fixedDepositAmount : mappedDepositValue;
    return activeTerm !== null && deposit !== null && priceValue !== null
      ? calcHitachiFinanceProperties(activeTerm, priceValue, deposit)
      : null;
  }, [activeTerm, mappedDepositValue, isFixedDepositSelected, fixedDepositAmount, priceValue]);

  const presentationalMonthlyRepayment = React.useMemo(() => {
    const zeroDepositTermsInRange = filterByDepositValue(terms, 0);
    const zeroDepositActiveTerm = zeroDepositTermsInRange[zeroDepositTermsInRange.length - 1] || null;
    return zeroDepositActiveTerm !== null && priceValue !== null
      ? calcHitachiFinanceProperties(zeroDepositActiveTerm, priceValue, zeroDepositActiveTerm.defaultDepositAmount)
          .monthlyRepayment
      : null;
  }, [priceValue, terms]);

  React.useEffect(
    /* eslint-disable-next-line prefer-arrow-callback */
    function getAndSetFinanceData() {
      const abortController = new AbortController();
      if (
        (totalPrice !== null && totalPrice !== prevTotalPriceRef.current) ||
        prevFinanceDetailsApiRef.current !== financeDetailsUrl
      ) {
        prevFinanceDetailsApiRef.current = financeDetailsUrl;
        prevTotalPriceRef.current = totalPrice;
        ajaxRequest({ method: 'GET', url: financeDetailsUrl, fields: { signal: abortController.signal } })
          .then((res) => {
            /* istanbul ignore else */
            if (res.available && isMountedRef.current) {
              const mappedTerms = mapByAddingMinAndMaxDepositAmount(res.terms, res.priceValue);
              const prevActiveTerm =
                mappedTerms.find(
                  (term) => res.financeChoiceData && term.code === res.financeChoiceData.hitachiProductCode
                ) || null;
              const nextActiveTerm =
                (selectedTerm && mappedTerms.find((term) => selectedTerm.code === term.code)) || prevActiveTerm;

              setTerms(mappedTerms);
              setSelectedTerm(
                nextActiveTerm && nextActiveTerm.type !== FinanceTermTypes.DeferredInterestFreeCredit
                  ? nextActiveTerm
                  : null
              );
              if (
                res.financeChoiceData &&
                res.financeChoiceData.financeDeposit !== null &&
                depositValueInRange === null
              ) {
                setDepositValue(res.financeChoiceData.financeDeposit);
                setIsFixedDepositSelected(!!res.financeChoiceData.fixedFinanceDeposit);
                const deferredTermDeposit = findDeferredTerm(mappedTerms, res.financeChoiceData.financeDeposit);
                setIsPaymentDeferred(
                  nextActiveTerm !== null && deferredTermDeposit !== null && nextActiveTerm === deferredTermDeposit
                );
              }
              setPriceValue(res.priceValue);
            }
          })
          .catch((e) => {
            console.error('Unable to fetch Hitachi Finance', e);
          });
      }
      return (): void => {
        abortController.abort();
      };
    },
    [
      financeDetailsUrl,
      setDepositValue,
      setSelectedTerm,
      selectedTerm,
      setIsFixedDepositSelected,
      depositValueInRange,
      setIsPaymentDeferred,
      totalPrice,
      priceValue
    ]
  );

  return React.useMemo(
    () => ({
      depositValue,
      mappedDepositValue,
      setDepositValue,
      selectedTerm: activeTerm,
      setSelectedTerm,
      isFixedDepositSelected,
      setIsFixedDepositSelected,
      isPaymentDeferred,
      setIsPaymentDeferred,
      fixedDepositAmount,
      termsInRage,
      deferredTerm,
      maxDeposit,
      depositEnabled,
      financeProperties,
      priceValue,
      presentationalMonthlyRepayment,
      financeType: PaymentType.HITACHI
    }),
    [
      depositValue,
      mappedDepositValue,
      activeTerm,
      isFixedDepositSelected,
      isPaymentDeferred,
      fixedDepositAmount,
      termsInRage,
      deferredTerm,
      maxDeposit,
      depositEnabled,
      financeProperties,
      priceValue,
      presentationalMonthlyRepayment
    ]
  );
}

export default useHitachiFinanceData;
